import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'

import { trackEvent } from '../../../../libs/analytics'
import buildSearchParams from '../../../../helpers/build-search-params'
import { getLocationFilterLabel } from '../../../../models/LocationFilter'
import extractUrlPath from '../../../../helpers/extract-url-path'

import {
  Container,
  Intro,
  SearchWrapper,
  BackgroundWrapper,
  Heading,
  HighlightedArtist,
  HighlightedArtistContent,
  HighlightedArtistHeading,
  HighlightedArtistLink,
  ArtistImage,
  TextColumnsWrapper,
  TextColumnsContainer,
  TextColumn,
  TextColumnHeading,
  TextColumnText,
} from './style'
import SearchForm from './components/search-form'
import HeroWave from './components/wave'

import { mediaQueriesDefinitions } from '../../../../styles/mediaQueries'
import { imageUrlFor } from '../../../../helpers/sanity-image-builder'
import SanityImage from '../../../sanity-image'

import LandingPageBlockWrapper from '../../../../ui/landing-page-block-wrapper'
import formatSimpleMarkdown from '../../../../helpers/format-simple-markdown'
import useOptions from '../../../../hooks/useOptions'

const HeroSectionBlock = React.memo(
  ({ title = '', intro = '', theme, artists = [], textColumns = [], anchorId = '' }) => {
    const history = useHistory()
    const { eventTypes } = useOptions()
    const [eventType, setEventType] = React.useState('')
    const [location, setLocation] = React.useState(null)
    const [randomNumber, setRandomNumber] = useState(undefined)

    useEffect(() => {
      setRandomNumber(Math.floor(Math.random() * artists.length))
    }, [])

    const handleEventTypeChange = (e) => {
      setEventType(e.currentTarget.value)
      trackEvent(
        'landingpage',
        'landingpage-block-header-changed-event-type',
        e.currentTarget.value,
      )
    }

    const handlePerformLocationChange = (match) => {
      setLocation(match)
      trackEvent(
        'landingpage',
        'landingpage-block-header-changed-perform-location',
        getLocationFilterLabel(match),
      )
    }

    const handlePerformLocationReset = () => {
      setLocation(null)
    }

    const handleSubmitSearch = () => {
      history.push(`/s${buildSearchParams(eventType, location)}`)
      trackEvent('landingpage', 'landingpage-block-header-submitted-search', 'Søk', {
        eventType,
        location: getLocationFilterLabel(location),
      })
    }
    const randomArtist = artists[randomNumber]
    return (
      <div>
        <LandingPageBlockWrapper theme={theme} anchorId={anchorId}>
          <BackgroundWrapper>
            <HeroWave />
          </BackgroundWrapper>
          <Container>
            {title ? (
              <Heading dangerouslySetInnerHTML={{ __html: formatSimpleMarkdown(title) }} />
            ) : null}
            {intro ? <Intro>{intro}</Intro> : null}
            {randomArtist ? (
              <HighlightedArtist
                narrow={
                  randomArtist.image && randomArtist.imageMetadata.dimensions.aspectRatio < 0.7
                    ? 1
                    : 0
                }
                medium={
                  randomArtist.image &&
                  randomArtist.imageMetadata.dimensions.aspectRatio < 0.7 &&
                  randomArtist.imageMetadata.dimensions.aspectRatio > 0.6
                    ? 1
                    : 0
                }
              >
                <ArtistImage>
                  <SanityImage
                    width={600}
                    image={randomArtist.image}
                    altText={randomArtist.image.altText}
                  >
                    <source
                      media={mediaQueriesDefinitions.mobileDown}
                      srcSet={imageUrlFor(randomArtist.image).width(600)}
                    />
                    <source
                      media={mediaQueriesDefinitions.tabletLandscapeDown}
                      srcSet={imageUrlFor(randomArtist.image).width(600)}
                    />
                    <source
                      media={mediaQueriesDefinitions.desktopDown}
                      srcSet={imageUrlFor(randomArtist.image).width(600)}
                    />
                  </SanityImage>
                </ArtistImage>
                <HighlightedArtistContent
                  color={
                    randomArtist.imageMetadata
                      ? randomArtist.imageMetadata.palette.vibrant.background
                      : undefined
                  }
                  medium={
                    randomArtist.image &&
                    randomArtist.imageMetadata.dimensions.aspectRatio < 0.7 &&
                    randomArtist.imageMetadata.dimensions.aspectRatio > 0.6
                      ? 1
                      : 0
                  }
                >
                  <HighlightedArtistHeading>Fremhevet Artist</HighlightedArtistHeading>
                  <HighlightedArtistLink to={extractUrlPath(randomArtist.url)}>
                    {randomArtist.name}
                  </HighlightedArtistLink>
                </HighlightedArtistContent>
              </HighlightedArtist>
            ) : null}
            <SearchWrapper>
              <SearchForm
                eventType={eventType}
                eventTypes={eventTypes}
                onEventTypeChange={handleEventTypeChange}
                performLocation={location}
                onPerformLocationChange={handlePerformLocationChange}
                onPerformLocationReset={handlePerformLocationReset}
                onSubmitSearch={handleSubmitSearch}
              />
            </SearchWrapper>
          </Container>
        </LandingPageBlockWrapper>
        <TextColumnsWrapper>
          <TextColumnsContainer>
            {textColumns
              ? textColumns.map((column) => {
                  return (
                    <TextColumn key={column._key}>
                      <TextColumnHeading>{column.heading}</TextColumnHeading>
                      <TextColumnText>{column.text}</TextColumnText>
                    </TextColumn>
                  )
                })
              : null}
          </TextColumnsContainer>
        </TextColumnsWrapper>
      </div>
    )
  },
)

HeroSectionBlock.propTypes = {
  title: PropTypes.string,
  theme: PropTypes.string.isRequired,
  anchorId: PropTypes.string,
  intro: PropTypes.string,
  artists: PropTypes.array,
  textColumns: PropTypes.array,
}

export default HeroSectionBlock
